import { Box } from "@mui/material";
import type { MRT_ColumnDef } from "material-react-table";
import { StoryActionButtons } from "Components/Stories/Management/StoryActionButtons";
import { StoryMediaThumbnail } from "Components/Stories/Management/StoryMediaThumbnail";
import { StoryStatusChip } from "Components/Stories/Management/StoryStatusChip";
import { type StoryListItemDto, StoryState } from "Infrastructure/Api/Api";
import { Resources, type TFunction } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";

const RESOURCES = Resources.Stories.Management;

/** Column ids MRT reports beyond the DTO keys (computed columns). */
export type StoryListExtraColumnId = "media" | "actions";

export type StoryListHandlers = {
  isPending: boolean;
  onPreview: (story: StoryListItemDto) => void;
  onPublish: (story: StoryListItemDto) => void;
  onUnpublish: (story: StoryListItemDto) => void;
  onDelete: (story: StoryListItemDto) => void;
};

export const createStoryListColumns = (
  t: TFunction,
  handlers: StoryListHandlers,
): Array<MRT_ColumnDef<StoryListItemDto>> => [
  {
    id: "media",
    header: "",
    size: 72,
    enableSorting: false,
    enableColumnFilter: false,
    Cell: ({ row }) => (
      <Box width={48}>
        <StoryMediaThumbnail
          story={row.original}
          aspectRatio="1 / 1"
          onClick={() => handlers.onPreview(row.original)}
        />
      </Box>
    ),
  },
  {
    accessorKey: "authorName",
    header: t(RESOURCES.Table.Author),
    size: 180,
  },
  {
    accessorKey: "text",
    header: t(RESOURCES.Table.Text),
    size: 260,
    Cell: ({ cell }) => (
      <Box
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {cell.getValue<string | null>() ?? ""}
      </Box>
    ),
  },
  {
    accessorKey: "state",
    header: t(RESOURCES.Table.State),
    size: 130,
    filterVariant: "select",
    filterSelectOptions: [
      { label: t(RESOURCES.Status.Draft), value: StoryState.Draft },
      { label: t(RESOURCES.Status.Live), value: StoryState.Live },
      { label: t(RESOURCES.Status.Expired), value: StoryState.Expired },
      {
        label: t(RESOURCES.Status.Unpublished),
        value: StoryState.Unpublished,
      },
    ],
    Cell: ({ row }) => <StoryStatusChip state={row.original.state} />,
  },
  {
    accessorKey: "lastPublishedAt",
    header: t(RESOURCES.Table.LastPublishedAt),
    size: 170,
    enableColumnFilter: false,
    Cell: ({ cell }) => formatDate(cell.getValue<string | null>(), true),
  },
  {
    accessorKey: "expiresAt",
    header: t(RESOURCES.Table.ExpiresAt),
    size: 170,
    enableColumnFilter: false,
    Cell: ({ row }) =>
      row.original.state === StoryState.Live
        ? formatDate(row.original.expiresAt, true)
        : "-",
  },
  {
    accessorKey: "publicationCount",
    header: t(RESOURCES.Table.Publications),
    size: 120,
    enableColumnFilter: false,
    enableSorting: false,
  },
  {
    accessorKey: "dateCreated",
    header: t(RESOURCES.Table.DateCreated),
    size: 170,
    enableColumnFilter: false,
    Cell: ({ cell }) => formatDate(cell.getValue<string>(), true),
  },
  {
    id: "actions",
    header: t(RESOURCES.Table.Actions),
    size: 180,
    enableColumnFilter: false,
    enableSorting: false,
    Cell: ({ row }) => (
      <StoryActionButtons
        story={row.original}
        isPending={handlers.isPending}
        onPreview={handlers.onPreview}
        onPublish={handlers.onPublish}
        onUnpublish={handlers.onUnpublish}
        onDelete={handlers.onDelete}
      />
    ),
  },
];
